import { ActionTree } from 'vuex';
import { IIntegrationState } from '@/settings/integrations/store/integrations.types';
import { RootState } from '@/store/store';
import { IIntegrationStatus } from '@/settings/integrations/types/integration-list-item';
import { IIntegrationModalDetails } from '@/settings/integrations/types/integration-modal';
import {
  EIntegrationActions,
  EIntegrationMutations,
  state as defaultState,
} from '@/settings/integrations/store/integrations.constants';
import { ILimeField } from '@/settings/integrations/types/integration-lime-field';
import { EIntegrationName } from '@/settings/integrations/types/enums/integration-name';

import { IntegrationService } from '@/api/settings/services/settings.service';
import { translate } from '@/helpers/translation.helper';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';

export const actions: ActionTree<IIntegrationState, RootState> = {
  [EIntegrationActions.SetModalMeta](
    { state, commit },
    newDetails: Partial<IIntegrationModalDetails>
  ) {
    commit(EIntegrationMutations.SetModalMeta, { ...state.details, ...newDetails });
  },

  async [EIntegrationActions.LoadStatus]({ commit, dispatch }) {
    commit(EIntegrationMutations.SetLoading, true);
    commit(EIntegrationMutations.SetLoaded, false);
    try {
      const integrationStatuses: IIntegrationStatus[] = await IntegrationService.loadStatuses();
      dispatch(EIntegrationActions.LoadStatusSuccess, integrationStatuses);
    } catch {
      dispatch(EIntegrationActions.LoadStatusFailed);
    }
  },
  [EIntegrationActions.LoadStatusSuccess]({ commit }, integrationStatuses: IIntegrationStatus[]) {
    commit(EIntegrationMutations.SetIntegrations, integrationStatuses);
    commit(EIntegrationMutations.SetLoading, false);
    commit(EIntegrationMutations.SetLoaded, true);
  },
  async [EIntegrationActions.LoadStatusFailed]({ commit, dispatch }) {
    commit(EIntegrationMutations.SetLoading, false);
    commit(EIntegrationMutations.SetLoaded, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-load-integrations'),
      {
        root: true,
      }
    );
  },
  [EIntegrationActions.Reset]({ commit }) {
    commit(EIntegrationMutations.SetState, defaultState);
  },
  [EIntegrationActions.ResetModalMeta]({ commit }) {
    commit(EIntegrationMutations.SetModalMeta, defaultState.details);
    commit(EIntegrationMutations.SetModalStep, defaultState.modalStep);
    commit(EIntegrationMutations.SetPhoneValidationCode, defaultState.phoneValidationCode);
    commit(EIntegrationMutations.SetLoading, defaultState.loading);
  },
  async [EIntegrationActions.SetDetails](
    { commit, dispatch },
    newDetails: IIntegrationModalDetails
  ) {
    commit(EIntegrationMutations.SetLoaded, false);
    commit(EIntegrationMutations.SetLoading, true);
    try {
      await IntegrationService.setDetails(newDetails);
      dispatch(EIntegrationActions.SetDetailsSuccess);
    } catch {
      dispatch(EIntegrationActions.SetDetailsFailed);
    }
  },
  [EIntegrationActions.SetDetailsSuccess]({ commit }) {
    commit(EIntegrationMutations.SetLoading, false);
    commit(EIntegrationMutations.SetLoaded, true);
  },
  [EIntegrationActions.SetDetailsFailed]({ commit, dispatch }) {
    commit(EIntegrationMutations.SetLoaded, false);
    commit(EIntegrationMutations.SetLoading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-save-integration-details-to-backend'),
      {
        root: true,
      }
    );
  },
  async [EIntegrationActions.LoadDetails]({ commit, dispatch }, integrationName: string) {
    try {
      commit(EIntegrationMutations.SetLoaded, false);
      commit(EIntegrationMutations.SetLoading, true);
      const integrationModalDetails: IIntegrationModalDetails = await IntegrationService.loadDetails(
        integrationName
      );
      dispatch(EIntegrationActions.LoadDetailsSuccess, integrationModalDetails);
    } catch {
      dispatch(EIntegrationActions.LoadDetailsFailed);
    }
  },
  [EIntegrationActions.LoadDetailsSuccess](
    { commit, dispatch },
    integrationModalDetails: IIntegrationModalDetails
  ) {
    dispatch(EIntegrationActions.SetModalMeta, integrationModalDetails);
    commit(EIntegrationMutations.SetLoading, false);
    commit(EIntegrationMutations.SetLoaded, true);
  },
  [EIntegrationActions.LoadDetailsFailed]({ commit, dispatch }) {
    commit(EIntegrationMutations.SetLoaded, false);
    commit(EIntegrationMutations.SetLoading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-load-integration-modal-details'),
      {
        root: true,
      }
    );
  },
  async [EIntegrationActions.Disconnect]({ commit, dispatch }, integrationName: string) {
    try {
      commit(EIntegrationMutations.SetLoaded, false);
      commit(EIntegrationMutations.SetLoading, true);
      await IntegrationService.disconnect(integrationName);
      dispatch(EIntegrationActions.DisconnectSuccess);
    } catch {
      dispatch(EIntegrationActions.DisconnectFailed);
    }
  },
  [EIntegrationActions.DisconnectSuccess]({ commit }) {
    commit(EIntegrationMutations.SetLoaded, true);
    commit(EIntegrationMutations.SetLoading, false);
  },
  [EIntegrationActions.DisconnectFailed]({ commit, dispatch }) {
    commit(EIntegrationMutations.SetLoaded, false);
    commit(EIntegrationMutations.SetLoading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-disconnect-integration'),
      {
        root: true,
      }
    );
  },
  async [EIntegrationActions.ValidateLimeCrmFields]({ commit, dispatch }, domainName: string) {
    commit(EIntegrationMutations.SetLoaded, false);
    commit(EIntegrationMutations.SetLoading, true);
    try {
      const fields: ILimeField[] = await IntegrationService.loadLimeCrmFields(domainName);
      dispatch(EIntegrationActions.ValidateLimeCrmFieldsSuccess, fields);
    } catch {
      dispatch(EIntegrationActions.ValidateLimeCrmFieldsFailed);
    }
  },
  [EIntegrationActions.ValidateLimeCrmFieldsSuccess]({ commit, dispatch }, fields: ILimeField[]) {
    dispatch(EIntegrationActions.SetModalMeta, { limeFields: fields });
    commit(EIntegrationMutations.SetLoaded, true);
    commit(EIntegrationMutations.SetLoading, false);
  },
  [EIntegrationActions.ValidateLimeCrmFieldsFailed]({ commit, dispatch }) {
    commit(EIntegrationMutations.SetLoaded, false);
    commit(EIntegrationMutations.SetLoading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      `${translate('Could not setup connection')} Lime CRM ${translate(
        'Check api-key and domain'
      )}`,
      {
        root: true,
      }
    );
  },
  async [EIntegrationActions.GenerateInputData]({ commit, dispatch }, name: EIntegrationName) {
    commit(EIntegrationMutations.SetLoaded, false);
    commit(EIntegrationMutations.SetLoading, true);
    let inputData: Partial<IIntegrationModalDetails>;
    try {
      switch (name) {
        case EIntegrationName.Sympa:
          inputData = await IntegrationService.generateSympaData();
          break;
        case EIntegrationName.SCIM:
          inputData = await IntegrationService.generateSCIMData();
          break;
        case EIntegrationName.EmailDocumentImport:
          inputData = await IntegrationService.generateEmailDocImpData();
          break;
      }
      dispatch(EIntegrationActions.GenerateInputDataSuccess, inputData);
    } catch (error) {
      dispatch(EIntegrationActions.GenerateInputDataFailed);
    }
  },
  [EIntegrationActions.GenerateInputDataSuccess](
    { commit, dispatch },
    inputData: Partial<IIntegrationModalDetails>
  ) {
    dispatch(EIntegrationActions.SetModalMeta, inputData);
    commit(EIntegrationMutations.SetLoading, false);
    commit(EIntegrationMutations.SetLoaded, true);
  },
  [EIntegrationActions.GenerateInputDataFailed]({ commit, dispatch }) {
    commit(EIntegrationMutations.SetLoading, false);
    commit(EIntegrationMutations.SetLoaded, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-generate-input-data'),
      {
        root: true,
      }
    );
  },
  async [EIntegrationActions.VerifyPhone]({ commit, dispatch }, payload: IIntegrationModalDetails) {
    commit(EIntegrationMutations.SetLoading, true);
    commit(EIntegrationMutations.SetLoaded, false);
    try {
      const validationCode: string = await IntegrationService.verifyPhone(payload);
      dispatch(EIntegrationActions.VerifyPhoneSuccess, validationCode);
    } catch (e) {
      dispatch(EIntegrationActions.VerifyPhoneFailed, e.message);
    }
  },
  [EIntegrationActions.VerifyPhoneSuccess]({ commit }, validationCode: string) {
    commit(EIntegrationMutations.SetLoaded, true);
    commit(EIntegrationMutations.SetPhoneValidationCode, validationCode);
    commit(EIntegrationMutations.SetModalStep, 2);
  },
  async [EIntegrationActions.VerifyPhoneFailed]({ commit, dispatch }, status: number) {
    commit(EIntegrationMutations.SetLoading, false);
    commit(EIntegrationMutations.SetLoaded, false);
    const phoneNumberAlreadyVerified = status === 21450;
    if (phoneNumberAlreadyVerified) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('phone-number-is-already-verified'),
        {
          root: true,
        }
      );
    } else {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('you-have-used-this-feature-too-many-times'),
        {
          root: true,
        }
      );
    }
  },
};
